import { openReferralModal } from "@src/components/referral/referral-modal";
import config from "@src/config";
import { PlanFamily, PlanStatus } from "@src/gql/graphql";
import { getTrialTimeRemaining } from "@src/utils/get-trial-days-remaining";
import type { ReactNode } from "react";

function Title({ children }: { children: ReactNode }) {
	return (
		<h1 className="text-2xl md:text-3xl font-bold tracking-tight text-text-primary mb-1.5">
			{children}
		</h1>
	);
}

function Description({ children }: { children: ReactNode }) {
	return (
		<p className="text-text-secondary text-[13px] md:text-sm max-w-md mx-auto leading-normal">
			{children}
		</p>
	);
}

function formatTrialCountdown(trialEnd: string | null): string {
	if (!trialEnd) return "a few moments";
	const remaining = getTrialTimeRemaining(trialEnd);
	if (!remaining) return "a few moments";
	const { value, unit } = remaining;
	const label = value === 1 ? unit : `${unit}s`;
	return `${value} ${label}`;
}

export function PlanModalHeader(props: {
	planFamily?: PlanFamily;
	status: PlanStatus;
	trialEnd?: string | null;
	featureName?: string;
}) {
	const { planFamily, status, trialEnd, featureName } = props;

	let variant:
		| "trial"
		| "trial-ended"
		| "expired"
		| "cancelled"
		| "plan"
		| "upgrade-to-pro"
		| "paywall"
		| "suspended" = "plan";

	if (status === PlanStatus.Paused) {
		variant = "suspended";
	} else if (status === PlanStatus.Cancelled) {
		variant = "cancelled";
	} else if (status === PlanStatus.Expired) {
		variant = "expired";
	} else if (status === PlanStatus.InTrial) {
		variant = "trial";
	} else if (status === PlanStatus.TrialEnd) {
		variant = "trial-ended";
	} else if (featureName) {
		variant = "paywall";
	} else if (
		planFamily === PlanFamily.Starter &&
		status === PlanStatus.Active
	) {
		variant = "upgrade-to-pro";
	}

	if (variant === "suspended") {
		return (
			<>
				<Title>Account Suspended</Title>
				<Description>
					Account suspended for Terms of Service violation. Please{" "}
					<a
						href={config.contactUsUrl}
						className="font-bold text-brand-primary hover:text-brand-primary-hover underline underline-offset-4"
						target="_blank"
						rel="noopener noreferrer"
					>
						contact support.
					</a>
				</Description>
			</>
		);
	}

	if (variant === "expired") {
		return (
			<>
				<Title>Plan Ended</Title>
				<Description>
					Please choose a plan to keep using illumi, or check if your billing
					details are up to date. You can also{" "}
					<button
						type="button"
						onClick={openReferralModal}
						className="font-bold text-brand-primary hover:text-brand-primary-hover underline underline-offset-4"
					>
						refer a friend
					</button>{" "}
					to earn more usage of illumi.
				</Description>
			</>
		);
	}

	if (variant === "cancelled") {
		return (
			<>
				<Title>Plan Cancelled</Title>
				<Description>
					You can still use illumi for PRO features until your next billing date
					or you can renew your plan to continue using illumi.
				</Description>
			</>
		);
	}

	if (variant === "trial") {
		const countdown = formatTrialCountdown(trialEnd ?? null);
		return (
			<>
				<Title>You have {countdown} left in your Trial ✨</Title>
				<Description>
					Choose a plan to keep your access after the trial ends.
				</Description>
			</>
		);
	}

	if (variant === "trial-ended") {
		return (
			<>
				<Title>Your trial has ended</Title>
				<Description>
					Everything you made is still here. Choose a plan to keep going — or{" "}
					<button
						type="button"
						onClick={openReferralModal}
						className="font-bold text-brand-primary hover:text-brand-primary-hover underline underline-offset-4"
					>
						refer a friend
					</button>{" "}
					and earn more usage.
				</Description>
			</>
		);
	}

	if (variant === "paywall") {
		return (
			<>
				<Title>Upgrade to Pro 🚀</Title>
				<Description>
					Unlock <strong>{featureName}</strong> and all Pro features.
				</Description>
			</>
		);
	}

	if (variant === "upgrade-to-pro") {
		return (
			<>
				<Title>Choose the plan that fits.</Title>
				<Description>More context. More AI. More of your team.</Description>
			</>
		);
	}

	return (
		<>
			<Title>Pricing</Title>
			<Description>
				Choose the plan that works best for you and your team.
			</Description>
		</>
	);
}
